import { motion } from "framer-motion";
import { ArrowRight, BookOpen } from "lucide-react";
import { Link, useNavigate } from "react-router-dom";
import CTAButton from "Util/CTAButton";

const month = (date, lang) =>
  new Date(date).toLocaleDateString(lang === "vi" ? "vi-VN" : "en-NZ", {
    month: "short",
  });

const fullDate = (date, lang) =>
  new Date(date).toLocaleDateString(lang === "vi" ? "vi-VN" : "en-NZ", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });

const BlogTimelineItem = ({ blog, lang, reverse, keyword }) => {
  const data = blog[lang];

  const excerpt = data.intro?.[0] || data.reflection?.[0] || "";

  const navigate = useNavigate();

  const highlight = (text) => {
    if (!keyword.trim()) return text;

    const regex = new RegExp(`(${keyword})`, "gi");

    return text.split(regex).map((part, i) =>
      regex.test(part) ? (
        <mark key={i} className="bg-neonYellow text-navy rounded px-1">
          {part}
        </mark>
      ) : (
        part
      ),
    );
  };

  return (
    <>
      {/* //mobile view  */}
      <motion.div
        initial={{ opacity: 0, y: 30 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        transition={{ duration: 0.5 }}
        className="relative lg:hidden pl-5 pb-24"
      >
        {/* timeline line */}
        <div className="absolute left-0 top-0 bottom-0 w-px bg-gray-300" />

        {/* circle */}
        <div className="absolute left-[-5px] top-2 w-3 h-3 rounded-full border-2 border-navy bg-[#faf8f5]" />

        {/* date */}
        <div
          className="
        inline-flex
        items-center
        justify-center
        min-w-[72px]
        rounded-full
        bg-neonYellow
        px-3 py-1.5
        text-[11px]
        font-bold
        uppercase
        tracking-[0.25em]
        text-white mr-2
      "
        >
          {month(blog.date, lang)}
        </div>
        <span className="text-xs tracking-[0.25em] uppercase text-gray-500 font-medium">
          {fullDate(blog.date, lang)}
        </span>

        {/* title */}
        <h2 className="mt-2 text-xl leading-[1.5] font-bold text-navy">
          {highlight(data.title)}
        </h2>

        {/* tags */}
        <div className="flex flex-wrap gap-2 mt-2">
          <span className="rounded bg-neonYellow/10 px-2 py-1 text-[11px] font-semibold text-neonYellow">
            Career
          </span>

          <span className="rounded bg-navy/5 px-2 py-1 text-[11px] font-semibold text-navy">
            Community
          </span>
        </div>

        {/* excerpt */}
        <div
          className="
      overflow-hidden
      rounded-3xl
      bg-white
      shadow-xl
      border
      border-gray-100
      hover:shadow-2xl
      transition-all
      duration-500 mt-6
    "
        >
          <img
            src={blog.mainImage}
            alt={data.title}
            className="
        w-full
        h-64
        object-cover
        transition-transform
        duration-700
        hover:scale-105
      "
          />
          <div className="p-4">
            <p className="mt-0 text-gray-600 leading-8 line-clamp-5">
              {highlight(excerpt)}
            </p>

            {/* link */}
            <button
              onClick={() => navigate(`/blog/${blog.id}`)}
              className="mt-6 inline-flex items-center gap-2 text-sm font-semibold text-navy hover:gap-3 transition-all"
            >
              {lang === "vi" ? "Đọc tiếp" : "Read More"}
              <ArrowRight size={16} />
            </button>
          </div>
        </div>
      </motion.div>

      {/* DESKTOP */}
      <motion.div
        initial={{ opacity: 0, y: 40 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        transition={{ duration: 0.5 }}
        className="
        hidden 
        relative
        lg:grid        
        lg:grid-cols-2
        gap-14
        items-center
      "
      >
        {/* Month Bubble */}
        <div
          className="
          hidden lg:flex
          absolute
          left-1/2
          top-1/2
          -translate-x-1/2
          -translate-y-1/2
          w-16
          h-16
          rounded-full
          bg-neonYellow
          text-white
          font-bold
          shadow-xl
          items-center
          justify-center
          z-10
          hover:text-amber
          hover:bg-navy
          transition
        "
        >
          {month(blog.date, lang)}
        </div>

        {/* LEFT */}
        <div
          className={`
    relative
    ${reverse ? "lg:order-2 lg:text-left" : "lg:text-right"}
    mx-0 lg:mx-5
  `}
        >
          <div className="flex items-center gap-4 mb-5 lg:hidden">
            <div
              className="
        inline-flex
        items-center
        justify-center
        min-w-[72px]
        rounded-full
        bg-neonYellow
        px-3 py-1.5
        text-[11px]
        font-bold
        uppercase
        tracking-[0.25em]
        text-white
      "
            >
              {month(blog.date, lang)}
            </div>

            {/* Date */}
            <span className="text-sm text-gray-500 font-medium">
              {fullDate(blog.date, lang)}
            </span>
          </div>

          {/* Category */}
          <div
            className={`
      flex flex-wrap gap-2 mb-5
      ${reverse ? "" : "lg:justify-end"}
    `}
          >
            <span className="rounded-full bg-neonYellow/10 px-3 py-1 text-xs font-semibold text-neonYellow">
              Career
            </span>

            <span className="rounded-full bg-navy/5 px-3 py-1 text-xs font-semibold text-navy">
              Community
            </span>
          </div>

          {/* Title */}
          <h2
            className="
      text-2xl
      lg:text-3xl
      font-bold
      leading-tight
      text-navy
      tracking-tight
    "
          >
            {highlight(data.title)}
          </h2>

          {/* Desktop Date */}
          <p
            className={`
      hidden lg:block
      mt-5
      text-sm
      font-medium
      uppercase
      tracking-[0.2em]
      text-gray-500
    `}
          >
            {fullDate(blog.date, lang)}
          </p>
        </div>

        <div className={`${reverse ? "lg:order-1" : ""} mx-0 lg:mx-5`}>
          <div
            className="
      overflow-hidden
      rounded-3xl
      bg-white
      shadow-xl
      border
      border-gray-100
      hover:shadow-2xl
      transition-all
      duration-500
    "
          >
            {/* Image */}
            <img
              src={blog.mainImage}
              alt={data.title}
              className="
        w-full
        h-64
        object-cover
        transition-transform
        duration-700
        hover:scale-105
      "
            />

            {/* Content */}
            <div className="p-4">
              <p
                className="
          text-gray-600
          leading-8
          line-clamp-5
          text-md
        "
              >
                {highlight(excerpt)}
              </p>

              <div className="mt-4">
                <CTAButton
                  variant="primary"
                  icon={BookOpen}
                  text={lang === "vi" ? "Đọc tiếp" : "Read More"}
                  onClick={() => navigate(`/blog/${blog.id}`)}
                />
              </div>
            </div>
          </div>
        </div>
      </motion.div>
    </>
  );
};

export default BlogTimelineItem;
